import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CalendarIcon, Clock, Users, Phone, Mail, User, CheckCircle2 } from 'lucide-react';
import useSEO from '@/hooks/useSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOUGH_IMAGE = "https://customer-assets.emergentagent.com/job_1bf35c11-3683-4ea1-b498-d0435b0fdb42/artifacts/pwpl3akd_Capture%20d%27%C3%A9cran%202026-04-21%20213055.png";

const Reservation = () => {
  useSEO({
    title: 'Réserver une table — NotaBene Sympho (Mons)',
    description: 'Réservez votre table en ligne au restaurant NotaBene Sympho à Mons. Confirmation rapide, ambiance chaleureuse, cuisine italienne authentique.',
    canonical: '/reservation',
  });
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    date: null,
    time: '',
    guests: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [blocks, setBlocks] = useState([]);

  useEffect(() => {
    axios.get(`${API}/reservation-blocks`)
      .then(res => setBlocks(res.data || []))
      .catch(() => setBlocks([]));
  }, []);

  const timeSlots = [
    '12:00', '12:15', '12:30', '12:45',
    '13:00', '13:15', '13:30',
    '18:00', '18:15', '18:30', '18:45',
    '19:00', '19:15', '19:30', '19:45',
    '20:00', '20:15', '20:30', '20:45',
    '21:00',
  ];

  const guestOptions = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10+'];

  // Filter time slots: if reserving for today, hide slots already passed
  // Additional rule: once a service has started (12h or 18h), block all slots of THAT service
  // (the client must reserve before service begins).
  const getAvailableTimeSlots = () => {
    if (!formData.date) return timeSlots;
    const dateStr = format(formData.date, 'yyyy-MM-dd');
    // Filter out slots blocked by admin
    const notBlockedSlots = timeSlots.filter((t) => {
      const [h, m] = t.split(':').map(Number);
      const slotMin = h * 60 + m;
      const period = (slotMin >= 12 * 60 && slotMin <= 13 * 60 + 30) ? 'lunch' : 'dinner';
      const blocked = blocks.find(b => b.date === dateStr && (b.period === period || b.period === 'all_day'));
      return !blocked;
    });

    const now = new Date();
    const isToday =
      formData.date.getFullYear() === now.getFullYear() &&
      formData.date.getMonth() === now.getMonth() &&
      formData.date.getDate() === now.getDate();
    if (!isToday) return notBlockedSlots;
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    const lunchStart = 12 * 60;   // 12h00
    const lunchEnd = 13 * 60 + 30; // 13h30 (inclus)
    const dinnerStart = 18 * 60;  // 18h00
    return notBlockedSlots.filter((t) => {
      const [h, m] = t.split(':').map(Number);
      const slotMin = h * 60 + m;
      // Hide strictly past slots
      if (slotMin <= nowMinutes) return false;
      // Block all lunch slots once lunch service has started (12h passed)
      if (nowMinutes >= lunchStart && slotMin >= lunchStart && slotMin <= lunchEnd) return false;
      // Block all dinner slots once dinner service has started (18h passed)
      if (nowMinutes >= dinnerStart && slotMin >= dinnerStart) return false;
      return true;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.email || !formData.date || !formData.time || !formData.guests) {
      toast.error('Veuillez remplir tous les champs obligatoires (email inclus)');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Veuillez entrer une adresse email valide');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/reservations`, {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        date: format(formData.date, 'yyyy-MM-dd'),
        time: formData.time,
        guests: parseInt(formData.guests),
        notes: formData.notes || null,
      });

      setSuccess(true);
      toast.success('Réservation confirmée !');
    } catch (error) {
      console.error('Error creating reservation:', error);
      const apiMsg = error?.response?.data?.detail;
      toast.error(apiMsg || 'Erreur lors de la réservation. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const isDateDisabled = (date) => {
    const day = date.getDay();
    // Disable Monday (1) and Tuesday (2). Allow today and future dates.
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (day === 1 || day === 2 || date < today) return true;
    // Disable dates where the WHOLE day is blocked by admin
    const dateStr = format(date, 'yyyy-MM-dd');
    return blocks.some(b => b.date === dateStr && b.period === 'all_day');
  };

  if (success) {
    return (
      <div className="pt-20 min-h-screen bg-[#F5F2EA] flex items-center justify-center" data-testid="reservation-success">
        <div className="max-w-lg mx-auto px-4 text-center">
          <div className="bg-white rounded-2xl p-12 shadow-lg">
            <div className="w-20 h-20 rounded-full bg-[#3A6351]/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-[#3A6351]" />
            </div>
            <h2 className="font-['Playfair_Display'] text-3xl font-bold text-[#1C1917] mb-4">
              Réservation confirmée !
            </h2>
            <p className="text-[#57534E] mb-4">
              Merci {formData.name} ! Votre table est réservée pour le{' '}
              <strong>{format(formData.date, 'EEEE d MMMM yyyy', { locale: fr })}</strong> à{' '}
              <strong>{formData.time}</strong> pour <strong>{formData.guests} personne(s)</strong>.
            </p>
            <div className="bg-[#FEF3C7] border border-[#F59E0B]/30 rounded-xl p-4 mb-6 text-left">
              <p className="text-[#78350F] text-sm font-semibold mb-1">
                📧 Vérifiez vos emails (et vos spams)
              </p>
              <p className="text-[#78350F] text-sm">
                Un email de confirmation vous a été envoyé. Pensez à le consulter <strong>absolument</strong> pour valider les détails de votre réservation.
              </p>
            </div>
            <Button
              onClick={() => {
                setSuccess(false);
                setFormData({
                  name: '',
                  phone: '',
                  email: '',
                  date: null,
                  time: '',
                  guests: '',
                  notes: '',
                });
              }}
              className="bg-[#C62E2E] hover:bg-[#a02424] text-white rounded-full px-8"
              data-testid="new-reservation-btn"
            >
              Nouvelle réservation
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-[#F5F2EA]" data-testid="reservation-page">
      {/* Header */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#C62E2E] font-medium tracking-widest uppercase mb-4">
            Réservation
          </p>
          <h1 className="font-['Playfair_Display'] text-5xl md:text-6xl font-bold text-[#1C1917] mb-6">
            Réserver une table
          </h1>
          <p className="text-[#57534E] text-lg max-w-2xl mx-auto">
            Réservez votre table en quelques clics pour une soirée inoubliable.
            Ouvert du mercredi au dimanche, midi et soir.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Form */}
            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg order-2 lg:order-1">
              <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#1C1917] mb-8">
                Informations de réservation
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name */}
                <div>
                  <Label htmlFor="name" className="flex items-center gap-2 mb-2">
                    <User className="w-4 h-4" />
                    Nom complet *
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Votre nom"
                    className="rounded-lg"
                    data-testid="reservation-name"
                    required
                  />
                </div>

                {/* Phone */}
                <div>
                  <Label htmlFor="phone" className="flex items-center gap-2 mb-2">
                    <Phone className="w-4 h-4" />
                    Téléphone *
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="Votre numéro de téléphone"
                    className="rounded-lg"
                    data-testid="reservation-phone"
                    required
                  />
                </div>

                {/* Email */}
                <div>
                  <Label htmlFor="email" className="flex items-center gap-2 mb-2">
                    <Mail className="w-4 h-4" />
                    Email *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Votre email (pour la confirmation)"
                    className="rounded-lg"
                    required
                    data-testid="reservation-email"
                  />
                  <p className="text-xs text-[#78716C] mt-1">
                    📧 Nous vous enverrons la confirmation par email.
                  </p>
                </div>

                {/* Date & Time */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Date */}
                  <div>
                    <Label className="flex items-center gap-2 mb-2">
                      <CalendarIcon className="w-4 h-4" />
                      Date *
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal rounded-lg"
                          data-testid="reservation-date"
                        >
                          {formData.date ? (
                            format(formData.date, 'PPP', { locale: fr })
                          ) : (
                            <span className="text-muted-foreground">Choisir une date</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.date}
                          onSelect={(date) => {
                            const newData = { ...formData, date };
                            // If date becomes today and previously chosen time is now passed, reset it
                            if (date && formData.time) {
                              const now = new Date();
                              const isToday =
                                date.getFullYear() === now.getFullYear() &&
                                date.getMonth() === now.getMonth() &&
                                date.getDate() === now.getDate();
                              if (isToday) {
                                const [h, m] = formData.time.split(':').map(Number);
                                if (h * 60 + m <= now.getHours() * 60 + now.getMinutes()) {
                                  newData.time = '';
                                }
                              }
                            }
                            setFormData(newData);
                          }}
                          disabled={isDateDisabled}
                          locale={fr}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Time */}
                  <div>
                    <Label className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4" />
                      Heure *
                    </Label>
                    <Select
                      value={formData.time}
                      onValueChange={(value) => setFormData({ ...formData, time: value })}
                    >
                      <SelectTrigger className="rounded-lg" data-testid="reservation-time">
                        <SelectValue placeholder="Choisir l'heure" />
                      </SelectTrigger>
                      <SelectContent>
                        {getAvailableTimeSlots().length === 0 ? (
                          <div className="px-3 py-4 text-sm text-[#57534E] text-center">
                            Aucun créneau disponible aujourd'hui.<br/>
                            Choisissez une autre date.
                          </div>
                        ) : (
                          getAvailableTimeSlots().map((time) => (
                            <SelectItem key={time} value={time}>
                              {time}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Guests */}
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4" />
                    Nombre de personnes *
                  </Label>
                  <Select
                    value={formData.guests}
                    onValueChange={(value) => setFormData({ ...formData, guests: value })}
                  >
                    <SelectTrigger className="rounded-lg" data-testid="reservation-guests">
                      <SelectValue placeholder="Nombre de personnes" />
                    </SelectTrigger>
                    <SelectContent>
                      {guestOptions.map((num) => (
                        <SelectItem key={num} value={num}>
                          {num} {parseInt(num) > 1 ? 'personnes' : 'personne'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Notes */}
                <div>
                  <Label htmlFor="notes" className="mb-2 block">
                    Remarques (optionnel)
                  </Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Allergies, occasion spéciale, demandes particulières..."
                    className="rounded-lg"
                    rows={3}
                    data-testid="reservation-notes"
                  />
                </div>

                {/* Submit */}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#C62E2E] hover:bg-[#a02424] text-white rounded-full py-6 text-lg font-semibold"
                  data-testid="reservation-submit"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <div className="spinner w-5 h-5 border-2 border-white/30 border-t-white"></div>
                      Réservation en cours...
                    </span>
                  ) : (
                    'Réserver ma table'
                  )}
                </Button>
              </form>

              <p className="text-[#57534E] text-sm mt-6 text-center">
                Fermé le lundi et mardi. Pour les groupes de plus de 10 personnes, 
                veuillez nous appeler au <a href="tel:+3265950649" className="text-[#C62E2E]">065 95 06 49</a>
              </p>
            </div>

            {/* Image */}
            <div className="order-1 lg:order-2">
              <div className="sticky top-32">
                <img
                  src={DOUGH_IMAGE}
                  alt="Façade du restaurant NotaBene Sympho"
                  className="rounded-2xl shadow-xl w-full"
                />
                <div className="bg-white rounded-2xl p-8 mt-6 shadow-lg">
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#1C1917] mb-4">
                    Horaires d'ouverture
                  </h3>
                  <ul className="space-y-2 text-[#57534E]">
                    <li className="flex justify-between">
                      <span>Lundi - Mardi</span>
                      <span className="text-[#C62E2E] font-medium">Fermé</span>
                    </li>
                    <li className="flex justify-between">
                      <span>Mercredi - Dimanche</span>
                      <span className="font-medium">12h - 14h30</span>
                    </li>
                    <li className="flex justify-between">
                      <span></span>
                      <span className="font-medium">18h - 22h</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Reservation;
